import { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { baseOptions, seriesColors, compactRankTick } from './setup.js';
import './Chart.css';

/*
 * Replaces the `chartdir` directive. The seven charts on the manager page
 * were previously built by $compile-ing directive elements from HTML
 * strings and appending them with jQuery (controllers.js:526-547); here
 * they are ordinary elements.
 *
 * `pointLabels` carries the per-point annotation the old build passed as
 * `compare-data-labels` (captain names). It renders in the tooltip rather
 * than as a datalabel on every point — 38 gameweeks times up to seven
 * managers made the old labelled charts unreadable, especially on a phone.
 */
export default function LineChart({
	title,
	xLabels,
	seriesLabels = [],
	series = [],
	pointLabels,
	logRankScale = false,
	height = 320,
}) {
	const { data, options } = useMemo(() => {
		const colors = seriesColors();

		const datasets = series
			/* Keep each series' original index so colours stay stable, then
			   drop the empty ones. A comparison series can be legitimately
			   empty — the 2025-26 OR-10K captain scores cannot be rebuilt,
			   since the FPL API no longer serves that season's picks — and an
			   empty series would otherwise add a legend entry with no line. */
			.map((values, i) => ({ values, i }))
			.filter(({ values }) => values?.length > 0)
			.map(({ values, i }) => ({
				label: seriesLabels[i] ?? `Series ${i + 1}`,
				data: values,
				borderColor: colors[i % colors.length],
				backgroundColor: colors[i % colors.length],
				borderWidth: 2,
				pointRadius: 2,
				pointHoverRadius: 5,
				tension: 0,
				fill: false,
			}));

		const opts = baseOptions();

		if (logRankScale) {
			opts.scales.y = {
				...opts.scales.y,
				type: 'logarithmic',
				min: 1,
				max: 10_000_000,
				ticks: { ...opts.scales.y.ticks, callback: compactRankTick },
			};
		}

		if (pointLabels) {
			opts.plugins.tooltip = {
				...opts.plugins.tooltip,
				callbacks: {
					afterLabel: (ctx) => {
						const label = pointLabels[ctx.datasetIndex]?.[ctx.dataIndex];
						return label ? `Captain: ${label}` : undefined;
					},
				},
			};
		}

		return { data: { labels: xLabels, datasets }, options: opts };
	}, [xLabels, seriesLabels, series, pointLabels, logRankScale]);

	return (
		<figure className="chart">
			<figcaption className="chart__title">{title}</figcaption>
			<div className="chart__canvas" style={{ height }}>
				<Line data={data} options={options} />
			</div>
		</figure>
	);
}
